import { formatDistanceToNowStrict } from 'date-fns';
import { RefreshCw } from 'lucide-react';

import { OUTREACH_REFRESH_INTERVAL_MS } from '@apps/web/components/admin/outreach_observability/constants';
import { SwarmWorkerHistoryChart } from '@apps/web/components/admin/outreach_observability/swarm_worker_history_chart';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Progress } from '@apps/web/components/ui/progress';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';
import { useAdminSwarmWorkerObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type SwarmWorkerStatus } from '@libraries/typescript/api_client';

type WorkerStatePresentation = {
    readonly label: string;
    readonly variant: 'success' | 'secondary' | 'outline';
};

function workerStatePresentation(
    state: SwarmWorkerStatus['runtime_state'],
): WorkerStatePresentation {
    switch (state) {
        case 'active':
            return { label: 'Active', variant: 'success' };
        case 'standby':
            return { label: 'Standby', variant: 'secondary' };
        case 'draining':
            return { label: 'Draining', variant: 'outline' };
        default:
            state satisfies never;
            throw new Error(`Unknown swarm worker state: ${state}`);
    }
}

function percentage(value: number, total: number): number {
    return total === 0 ? 0 : (value / total) * 100;
}

function CapacityCard({
    title,
    value,
    description,
    warning = false,
}: {
    readonly title: string;
    readonly value: string;
    readonly description: string;
    readonly warning?: boolean;
}) {
    return (
        <Card className="gap-3 py-4">
            <CardHeader className="px-4">
                <CardDescription>{title}</CardDescription>
                <CardTitle
                    className={cn(
                        'text-2xl tabular-nums',
                        warning && 'text-amber-600 dark:text-amber-400',
                    )}
                >
                    {value}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground px-4 text-xs">
                {description}
            </CardContent>
        </Card>
    );
}

function WorkerRow({ worker }: { readonly worker: SwarmWorkerStatus }) {
    const presentation = workerStatePresentation(worker.runtime_state);
    const utilization = percentage(worker.active_decisions, worker.capacity);
    const processLabel = (
        worker.worker_id.split(':')[0] ?? worker.worker_id
    ).slice(0, 12);

    return (
        <div className="grid gap-3 border-b px-4 py-3 last:border-b-0 md:grid-cols-[minmax(10rem,1fr)_7rem_minmax(14rem,2fr)_10rem] md:items-center">
            <div className="min-w-0">
                <div
                    className="truncate font-mono text-sm font-medium"
                    title={worker.worker_id}
                >
                    {processLabel}
                </div>
                <div className="text-muted-foreground text-xs">
                    updated{' '}
                    {formatDistanceToNowStrict(new Date(worker.observed_at), {
                        addSuffix: true,
                    })}
                </div>
            </div>
            <Badge variant={presentation.variant}>{presentation.label}</Badge>
            <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-muted-foreground">
                        Decision slots
                    </span>
                    <span className="font-medium tabular-nums">
                        {worker.active_decisions} / {worker.capacity}
                    </span>
                </div>
                <Progress
                    value={utilization}
                    indicatorClassName={cn(
                        utilization >= 90
                            ? 'bg-red-500'
                            : utilization >= 70
                              ? 'bg-amber-500'
                              : 'bg-emerald-500',
                    )}
                />
            </div>
            <div className="text-right text-sm font-medium tabular-nums">
                {utilization.toFixed(0)}% utilized
            </div>
        </div>
    );
}

export function SwarmWorkersTab() {
    const { data, hasRequestFailed, isLoading, isValidating, mutate } =
        useAdminSwarmWorkerObservability({
            refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS,
        });

    if (isLoading && data === undefined) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center gap-2 text-sm">
                <Spinner size="sm" />
                Loading swarm worker capacity…
            </div>
        );
    }

    if (hasRequestFailed && data === undefined) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Worker capacity unavailable</CardTitle>
                    <CardDescription>
                        The live swarm worker status could not be loaded.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void mutate()}
                    >
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const workers = data?.workers ?? [];
    const activeWorkers = workers.filter(
        (worker) => worker.runtime_state === 'active',
    );
    const processingDecisions = workers.reduce(
        (total, worker) => total + worker.active_decisions,
        0,
    );
    const readyCapacity = activeWorkers.reduce(
        (total, worker) => total + worker.capacity,
        0,
    );
    const current = data?.current;
    const utilization = percentage(processingDecisions, readyCapacity);
    const reportingMismatch =
        current !== null &&
        current !== undefined &&
        current.running_workers !== workers.length;

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-base font-semibold">
                        Recommendation swarm workers
                    </h2>
                    <p className="text-muted-foreground text-sm">
                        Fleet-wide process capacity. Organization and outreach
                        window filters do not apply.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void mutate()}
                    disabled={isValidating}
                >
                    {isValidating ? <Spinner size="sm" /> : <RefreshCw />}
                    Refresh
                </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <CapacityCard
                    title="Ready workers"
                    value={`${activeWorkers.length} / ${
                        current?.desired_workers ?? '—'
                    }`}
                    description={
                        current === null || current === undefined
                            ? 'Waiting for the first ECS fleet sample.'
                            : `${current.running_workers} ECS running · ${current.pending_workers} pending`
                    }
                    warning={reportingMismatch}
                />
                <CapacityCard
                    title="Decision slots in use"
                    value={`${processingDecisions} / ${readyCapacity}`}
                    description="Live model evaluations across reporting processes."
                    warning={
                        readyCapacity > 0 &&
                        processingDecisions >= readyCapacity
                    }
                />
                <CapacityCard
                    title="Waiting for a slot"
                    value={
                        current === null || current === undefined
                            ? '—'
                            : String(current.queued_decisions)
                    }
                    description={
                        current === null || current === undefined
                            ? 'Waiting for the first demand sample.'
                            : `${current.decision_demand} queued or running decisions total.`
                    }
                    warning={(current?.queued_decisions ?? 0) > 0}
                />
                <CapacityCard
                    title="Ready-slot utilization"
                    value={
                        readyCapacity === 0 ? '—' : `${utilization.toFixed(0)}%`
                    }
                    description={
                        current?.deployment_in_progress === true
                            ? 'ECS deployment is currently in progress.'
                            : 'Processing decisions divided by ready process slots.'
                    }
                    warning={
                        utilization >= 90 ||
                        current?.deployment_in_progress === true
                    }
                />
            </div>
            {reportingMismatch ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                    ECS reports {current.running_workers} running tasks, but{' '}
                    {workers.length} worker processes are reporting. A deploy,
                    startup, or drain may be in progress.
                </div>
            ) : null}
            <Card className="gap-3">
                <CardHeader>
                    <CardTitle>Live worker processes</CardTitle>
                    <CardDescription>
                        Each process reports its configured asynchronous
                        recommendation decision slots. Standby and draining
                        processes stay visible but do not count as ready
                        capacity.
                    </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                    {workers.length === 0 ? (
                        <div className="text-muted-foreground px-6 py-10 text-center text-sm">
                            No swarm worker processes are reporting yet.
                        </div>
                    ) : (
                        workers.map((worker) => (
                            <WorkerRow key={worker.worker_id} worker={worker} />
                        ))
                    )}
                </CardContent>
            </Card>
            <SwarmWorkerHistoryChart history={data?.history ?? []} />
        </div>
    );
}
